import { serverUpdateTimerDuration } from "api/api-server-actions";
import { t } from "i18n/i18n";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useCapabilities } from "store/capability-hooks";
import { store, useAppSelector } from "store/index";
import {
  selectActiveClassSessionId,
  selectClassTimerEndsAt,
  selectCurrentSectionIsArchived,
  selectIsTimeline,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import { selectCurrentClassUserIsInPersonClass } from "store/selectors/class-user";
import { toastLocalizedOperationCatcher } from "utils/alerts";

export const InPersonGroupTimerButton = ({
  startWithSavedDuration,
  isDisabled,
}: {
  startWithSavedDuration: (start: (duration: number) => Promise<void>) => Promise<void>;
  isDisabled?: boolean;
}) => {
  const { canAuthorSection } = useCapabilities();
  const classSessionId = useAppSelector(selectActiveClassSessionId);
  const isInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isArchived = useAppSelector(selectCurrentSectionIsArchived);
  const isTimeline = useAppSelector(selectIsTimeline);
  const timerEndsAt = useAppSelector(selectClassTimerEndsAt);
  const isInBreakout = useAppSelector(selectIsInBreakout);

  if (!canAuthorSection || !classSessionId || !isInPersonClass || isArchived || isTimeline) {
    return null;
  }

  const startTimer = async () => {
    try {
      await startWithSavedDuration(async (duration) => {
        const state = store.getState();
        // The class or timer may change while the edited duration is saving.
        if (
          selectActiveClassSessionId(state) !== classSessionId ||
          !selectCurrentClassUserIsInPersonClass(state) ||
          selectCurrentSectionIsArchived(state) ||
          selectIsTimeline(state) ||
          selectClassTimerEndsAt(state) ||
          selectIsInBreakout(state)
        )
          return;
        await serverUpdateTimerDuration(classSessionId, duration * 60);
      });
    } catch (error) {
      toastLocalizedOperationCatcher("failed_to_set_timer")(error);
    }
  };

  return (
    <SquareButton
      disabled={!!timerEndsAt || isInBreakout || isDisabled}
      eventName="Group Settings - Start timer"
      kind="accent"
      size="s"
      data-saves-duration
      disableWhilePending
      onClick={startTimer}
    >
      {t("footer.tool.timer_start")}
    </SquareButton>
  );
};
